import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

function PasswordInput({ id, testId, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        data-testid={testId}
        type={show ? "text" : "password"}
        className={`${inputCls} pr-12`}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        minLength={8}
        required
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-[#e6b800]"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function ForgotPassword() {
  const [form, setForm] = useState({ email: "", phone: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("दोनों passwords match नहीं कर रहे");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        "/auth/customer/reset-password",
        { email: form.email, phone: form.phone, new_password: form.password },
        { withCredentials: true }
      );
      setDone(true);
      toast.success(data.message || "Password set हो गया");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-28" data-testid="forgot-password-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="forgot-password-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <KeyRound size={20} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold uppercase text-white">Reset password</h1>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">
          अपना email और booking का phone number डालें — phone आपकी booking से match होना चाहिए। कोई email नहीं भेजा जाता।
        </p>

        {done ? (
          <div className="mt-7 rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/5 p-5" data-testid="forgot-success">
            <p className="flex items-start gap-3 text-sm leading-relaxed text-[#e6b800]">
              <ShieldCheck size={16} className="mt-0.5 shrink-0" />
              Password set हो गया। अब नये password से login करें।
            </p>
            <Link
              to="/account"
              data-testid="forgot-login-link"
              className="mt-4 inline-block rounded-full bg-[#e6b800] px-6 py-2.5 text-sm font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700]"
            >
              Login करें
            </Link>
          </div>
        ) : (
          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="fp-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                Email
              </label>
              <input id="fp-email" data-testid="forgot-email-input" type="email" className={inputCls} placeholder="aap@example.com" value={form.email} onChange={set("email")} required />
            </div>
            <div>
              <label htmlFor="fp-phone" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                Booking à¤à¤¾ phone number
              </label>
              <input id="fp-phone" data-testid="forgot-phone-input" type="tel" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} minLength={7} required />
            </div>
            <div>
              <label htmlFor="fp-pass" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                नया password (min 8 characters)
              </label>
              <PasswordInput id="fp-pass" testId="forgot-new-password-input" value={form.password} onChange={set("password")} />
            </div>
            <div>
              <label htmlFor="fp-confirm" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                Confirm password
              </label>
              <PasswordInput id="fp-confirm" testId="forgot-confirm-password-input" value={form.confirm} onChange={set("confirm")} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="w-full rounded-full bg-[#e6b800] py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Password set करें"}
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-[11px] text-neutral-500">
          <Link to="/account" data-testid="forgot-back-to-login" className="inline-flex items-center gap-1.5 text-[#e6b800] hover:underline">
            <ArrowLeft size={12} /> Login पर वापस जाएँ
          </Link>
        </p>
      </motion.form>
    </div>
  );
}
